import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import { CustomAxiosRequestConfig } from 'src/api/axiosClient/types';
import { getSignupToken, SIGNUP_SESSION_HEADER } from '../helpers/signupSession';
import {
  SignupAccountParams,
  SignupAreaCodesResponse,
  SignupAvailableNumbersResponse,
  SignupCompanyInfoParams,
  SignupConfirmParams,
  SignupFormOptionsResponse,
  SignupPlansResponse,
  SignupNumberSearchParams,
  SignupStartParams,
  SignupStartedResponse,
  SignupState,
} from '../types';

type LoginResponse = {
  accessToken: string;
  refreshToken: string;
};

/**
 * Every funnel screen renders its own failures — a disabled application, an expired code, a plan that is
 * gone — so the client's generic toast would either duplicate the message or fire after we have already
 * navigated away.
 */
const requestConfig: CustomAxiosRequestConfig = { skipGenericToast: true };

// A function, unlike the above: the token is stored during the funnel, so it has to be read per request.
const sessionRequestConfig = (): CustomAxiosRequestConfig => ({
  ...requestConfig,
  headers: { [SIGNUP_SESSION_HEADER]: getSignupToken() ?? '' },
});

/**
 * The second half of the funnel is behind the login, so those calls carry the ordinary JWT and name the
 * company in the path — the funnel session token is gone by then. They still suppress the generic toast:
 * the screens render their own failures, and a 409 on a taken number is a navigation, not a toast.
 *
 * Those calls are unusable without a company: unchecked, a missing one reaches the API as the literal
 * `/signup/companies/undefined/…` and comes back as a validation complaint about the path.
 */
export function useSignupApi(companyId?: string) {
  return useMemo(() => {
    const company = (): string => {
      if (companyId == null || companyId.length == 0) {
        throw new Error('Signup company route called without a company');
      }
      return companyId;
    };

    return {
      getPlans: () => {
        return apiClient.get<SignupPlansResponse>(`/signup/plans`, requestConfig);
      },

      getFormOptions: () => {
        return apiClient.get<SignupFormOptionsResponse>(`/signup/form-options`, requestConfig);
      },

      getState: () => {
        return apiClient.get<SignupState>(`/signup/state`, sessionRequestConfig());
      },

      // The token is passed in rather than stored first: one the API turns out not to recognise would be left
      // behind in `localStorage`, and every later visit to the plan screen would look for the funnel it names.
      getStateWithToken: (sessionToken: string) => {
        return apiClient.get<SignupState>(`/signup/state`, {
          ...requestConfig,
          headers: { [SIGNUP_SESSION_HEADER]: sessionToken },
        });
      },

      start: (params: SignupStartParams) => {
        return apiClient.post<SignupStartedResponse>(`/signup`, params, requestConfig);
      },

      saveAccount: (params: SignupAccountParams) => {
        return apiClient.put<SignupState>(`/signup/account`, params, sessionRequestConfig());
      },

      // Answers with a login: this is the step that creates the company, the brand and the admin user.
      saveCompanyInfo: (params: SignupCompanyInfoParams) => {
        return apiClient.put<LoginResponse>(`/signup/company-info`, params, sessionRequestConfig());
      },

      sendVerification: () => {
        return apiClient.post<void>(`/signup/email-verification`, {}, sessionRequestConfig());
      },

      // Answers with the funnel session rather than a login: no account exists at this point.
      confirmCode: (code: string) => {
        const params: SignupConfirmParams = { code: code };
        return apiClient.post<SignupStartedResponse>(
          `/signup/email-verification/confirm`,
          params,
          sessionRequestConfig(),
        );
      },

      // No session header on purpose: the emailed link is opened wherever the mail was read, and that
      // browser has never held the funnel session.
      confirmLink: (linkToken: string) => {
        const params: SignupConfirmParams = { linkToken: linkToken };
        return apiClient.post<SignupStartedResponse>(
          `/signup/email-verification/confirm`,
          params,
          requestConfig,
        );
      },

      // For a browser still holding the funnel session after the email was confirmed somewhere else. Both
      // credentials from the email are single-use and already spent by then, so there is nothing left to
      // confirm — the session token is what earns the login, and only for a short while after confirmation.
      resumeSession: () => {
        return apiClient.post<LoginResponse>(`/signup/session`, {}, sessionRequestConfig());
      },

      // The same answer as `getState`, for the steps that run after the session token has been dropped.
      getCompanyState: () => {
        return apiClient.get<SignupState>(`/signup/companies/${company()}/state`, requestConfig);
      },

      // Which area codes the chosen state actually has numbers in, and how many behind each. Read before
      // the search so the picker offers codes that lead somewhere.
      listAreaCodes: (state: string) => {
        return apiClient.get<SignupAreaCodesResponse>(
          `/signup/companies/${company()}/area-codes`,
          { ...requestConfig, params: { state: state } },
        );
      },

      searchPhoneNumbers: (params: SignupNumberSearchParams) => {
        return apiClient.get<SignupAvailableNumbersResponse>(
          `/signup/companies/${company()}/phone-numbers`,
          { ...requestConfig, params: params },
        );
      },

      reservePhoneNumber: (phoneNumber: string) => {
        return apiClient.post<SignupState>(
          `/signup/companies/${company()}/phone-number`,
          { phoneNumber: phoneNumber },
          requestConfig,
        );
      },

      checkout: () => {
        return apiClient.post<SignupState>(`/signup/companies/${company()}/checkout`, {}, requestConfig);
      },

      retryPayment: () => {
        return apiClient.post<SignupState>(`/signup/companies/${company()}/retry-payment`, {}, requestConfig);
      },

      finish: () => {
        return apiClient.post<SignupState>(`/signup/companies/${company()}/finish`, {}, requestConfig);
      },
    };
  }, [companyId]);
}
